import { Controller } from "@hotwired/stimulus";

// Landing page (V12) animations.
// Attached to the .elron-lp-v12 root via data-controller="landing".
// Drives: scroll reveals, count-up metrics, the hero pipeline loop,
// the work-ticket loop, the pricing simulator, and the FAQ accordion.
// Everything is scoped to this.element and cleaned up on disconnect so
// it survives htmx boosted navigation without leaking timers/observers.
export default class extends Controller<HTMLElement> {
    private reducedMotion = false;
    private observers: IntersectionObserver[] = [];
    private timers: number[] = [];
    private cleanups: Array<() => void> = [];

    connect(): void {
        this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        this.setupReveals();
        this.setupCounters();
        this.setupHeroLoop();
        this.setupWorkLoop();
        this.setupSimulator();
        this.setupFAQ();
    }

    disconnect(): void {
        this.observers.forEach((observer) => observer.disconnect());
        this.observers = [];
        this.timers.forEach((timer) => window.clearTimeout(timer));
        this.timers = [];
        this.cleanups.forEach((fn) => fn());
        this.cleanups = [];
    }

    private $<T extends Element = HTMLElement>(selector: string): T | null {
        return this.element.querySelector<T>(selector);
    }

    private $$<T extends Element = HTMLElement>(selector: string): T[] {
        return [...this.element.querySelectorAll<T>(selector)];
    }

    /* ---------- Reveal on scroll ---------- */

    private setupReveals(): void {
        const targets = this.$$(".reveal");
        if (this.reducedMotion || !("IntersectionObserver" in window)) {
            targets.forEach((el) => el.classList.add("in-view"));
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("in-view");
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.18 },
        );
        targets.forEach((el) => observer.observe(el));
        this.observers.push(observer);
    }

    /* ---------- Count-up metrics ---------- */

    private animateCount(el: HTMLElement): void {
        const value = parseFloat(el.dataset.value || "0");
        const decimals = parseInt(el.dataset.decimals || "0", 10) || 0;
        const prefix = el.dataset.prefix || "";
        const suffix = el.dataset.suffix || "";
        const format = (n: number): string => prefix + n.toFixed(decimals).replace(".", ",") + suffix;
        if (this.reducedMotion) {
            el.textContent = format(value);
            return;
        }
        const duration = 1300;
        const start = performance.now();
        const frame = (now: number): void => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            el.textContent = format(value * eased);
            if (progress < 1) requestAnimationFrame(frame);
        };
        requestAnimationFrame(frame);
    }

    private setupCounters(): void {
        const counters = this.$$(".count-up");
        if (!("IntersectionObserver" in window)) {
            counters.forEach((el) => this.animateCount(el));
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.animateCount(entry.target as HTMLElement);
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.5 },
        );
        counters.forEach((el) => observer.observe(el));
        this.observers.push(observer);
    }

    /* ---------- Hero pipeline loop ---------- */

    private setupHeroLoop(): void {
        if (this.reducedMotion) return;
        const pipeline = this.$('[data-lp="pipeline"]');
        if (!pipeline) return;
        const sources = [...pipeline.querySelectorAll<HTMLElement>(".source-card")];
        const autoChip = pipeline.querySelector<HTMLElement>('[data-lp="auto-chip"]');
        const ticket = pipeline.querySelector<HTMLElement>(".ticket-card");
        const actionCards = [...pipeline.querySelectorAll<HTMLElement>(".action-card")];
        const counter = pipeline.querySelector<HTMLElement>('[data-lp="plan-counter"]');
        const validate = pipeline.querySelector<HTMLElement>('[data-lp="validate-btn"]');
        if (!autoChip || !ticket || !counter || !validate) return;

        let timers: number[] = [];
        const schedule = (fn: () => void, delay: number): void => {
            timers.push(window.setTimeout(fn, delay));
        };

        const cycle = (): void => {
            timers.forEach((timer) => window.clearTimeout(timer));
            timers = [];
            sources.forEach((el) => el.classList.remove("src-active"));
            autoChip.classList.remove("chip-active");
            actionCards.forEach((el) => el.classList.remove("done"));
            ticket.classList.remove("tick-pop");
            counter.textContent = "0 / 4";
            validate.classList.remove("btn-pulse");

            sources.forEach((el, index) => schedule(() => el.classList.add("src-active"), 500 + index * 550));
            schedule(() => autoChip.classList.add("chip-active"), 2100);
            schedule(() => ticket.classList.add("tick-pop"), 2500);
            actionCards.forEach((el, index) =>
                schedule(() => {
                    el.classList.add("done");
                    counter.textContent = `${index + 1} / 4`;
                }, 3500 + index * 800),
            );
            schedule(() => validate.classList.add("btn-pulse"), 6900);
            schedule(cycle, 9400);
        };

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        cycle();
                    } else {
                        timers.forEach((timer) => window.clearTimeout(timer));
                        timers = [];
                    }
                });
            },
            { threshold: 0.25 },
        );
        observer.observe(pipeline);
        this.observers.push(observer);
        this.cleanups.push(() => timers.forEach((timer) => window.clearTimeout(timer)));
    }

    /* ---------- Work-ticket loop ---------- */

    private setupWorkLoop(): void {
        const card = this.$('[data-lp="work-ticket"]');
        if (!card) return;
        const steps = [...card.querySelectorAll<HTMLElement>(".wt-step")];
        const actions = [...card.querySelectorAll<HTMLElement>(".wt-action")];
        const status = card.querySelector<HTMLElement>('[data-lp="wt-status"]');
        const statusDefault = card.querySelector<HTMLElement>('[data-lp="wt-status-default"]');
        const statusResolved = card.querySelector<HTMLElement>('[data-lp="wt-status-resolved"]');
        const launch = card.querySelector<HTMLElement>('[data-lp="wt-launch"]');
        if (!status || !statusDefault || !statusResolved || !launch) return;

        if (this.reducedMotion) {
            steps.forEach((el) => el.classList.add("wt-visible"));
            return;
        }

        let timers: number[] = [];
        const schedule = (fn: () => void, delay: number): void => {
            timers.push(window.setTimeout(fn, delay));
        };

        const cycle = (): void => {
            timers.forEach((timer) => window.clearTimeout(timer));
            timers = [];
            steps.forEach((el) => el.classList.remove("wt-visible"));
            actions.forEach((el) => el.classList.remove("done"));
            status.classList.remove("wt-resolved");
            statusDefault.style.display = "";
            statusResolved.style.display = "none";
            launch.classList.remove("btn-pulse");

            steps.forEach((el, index) => schedule(() => el.classList.add("wt-visible"), 350 + index * 600));
            schedule(() => launch.classList.add("btn-pulse"), 4400);
            actions.forEach((el, index) => schedule(() => el.classList.add("done"), 5600 + index * 650));
            schedule(() => {
                status.classList.add("wt-resolved");
                statusDefault.style.display = "none";
                statusResolved.style.display = "inline-flex";
            }, 7800);
            schedule(cycle, 11000);
        };

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        cycle();
                    } else {
                        timers.forEach((timer) => window.clearTimeout(timer));
                        timers = [];
                    }
                });
            },
            { threshold: 0.3 },
        );
        observer.observe(card);
        this.observers.push(observer);
        this.cleanups.push(() => timers.forEach((timer) => window.clearTimeout(timer)));
    }

    /* ---------- Pricing simulator ---------- */

    private setupSimulator(): void {
        const simulator = this.$<HTMLElement>('[data-lp="simulator"]');
        const slider = this.$<HTMLInputElement>('[data-lp="sim-range"]');
        if (!simulator || !slider) return;
        const lotsValue = this.$('[data-lp="sim-lots-value"]');
        const price = this.$('[data-lp="sim-price"]');
        const capacityValue = this.$('[data-lp="sim-capacity-value"]');
        const revenue = this.$('[data-lp="sim-revenue"]');
        if (!lotsValue || !price || !capacityValue || !revenue) return;
        const locale =
            document.documentElement.dataset.elronLocale || document.documentElement.lang || "fr-FR";
        const currency = simulator.dataset.lpCurrency || "EUR";
        const unitPrice = Number(simulator.dataset.lpUnitPrice || "1");
        const averageMonthlyRent = Number(simulator.dataset.lpAverageRent || "1000");
        const managementFeeBasisPoints = Number(simulator.dataset.lpManagementFeeBps || "600");
        const format = (n: number): string => n.toLocaleString(locale);
        const formatCurrency = new Intl.NumberFormat(locale, {
            style: "currency",
            currency,
            maximumFractionDigits: 0,
        }).format;
        const update = (): void => {
            const lots = parseInt(slider.value, 10);
            const extraLots = Math.round((lots * 0.5) / 10) * 10;
            const monthlyRevenue =
                extraLots * averageMonthlyRent * (managementFeeBasisPoints / 10000);
            lotsValue.textContent = format(lots);
            price.textContent = formatCurrency(lots * unitPrice);
            capacityValue.textContent = format(extraLots);
            revenue.textContent = formatCurrency(monthlyRevenue);
        };
        slider.addEventListener("input", update);
        this.cleanups.push(() => slider.removeEventListener("input", update));
        update();
    }

    /* ---------- FAQ accordion (close others on open) ---------- */

    private setupFAQ(): void {
        const items = this.$$<HTMLDetailsElement>(".faq-item");
        items.forEach((item) => {
            const summary = item.querySelector("summary");
            if (!summary) return;
            const handler = (): void => {
                items.forEach((other) => {
                    if (other !== item) other.removeAttribute("open");
                });
            };
            summary.addEventListener("click", handler);
            this.cleanups.push(() => summary.removeEventListener("click", handler));
        });
    }
}
